import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import {
  FormField,
  H1,
  Button,
  Page, DateField,
} from 'rf-ui';
import { isAuthorized } from 'rf-lib';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { games as gamesAPI } from 'rf-api';
import { NotAuthorized, RemoteData } from 'rf-comp';
import * as paths from 'rf-lib/routePaths';
import GameFormDetails from './GameFormDetails';
import * as a from './actions';

const NewGame = ({ authUser, fetchGroupDetails, groupDetailsRemoteData }) => {
  const { groupId } = useParams();
  const [groupFetchStarted, setGroupFetchStarted] = useState(false);

  if (!isAuthorized('admin', groupId, authUser)) {
    return <NotAuthorized message="You are not authorized to create a new game in this group" />;
  }

  useEffect(() => {
    fetchGroupDetails(groupId);
    setGroupFetchStarted(true);
  }, [fetchGroupDetails, groupId]);

  return (
    <Page>
      {!groupFetchStarted && <span>Henter gruppedetaljer...</span>}
      {groupFetchStarted && (
      <RemoteData
        type={groupDetailsRemoteData}
        notAsked={() => <span>Henter gruppedetaljer...</span>}
        loading={() => <span>Henter gruppedetaljer...</span>}
        failure={err => <span>Feil: {err.message}</span>}
        success={group => (
          group?.id === groupId ? (
          <GameForm
            key={`new-game-${groupId}-${JSON.stringify(group?.defaults || {})}`}
            groupId={groupId}
            groupDefaults={group?.defaults}
            showAdvancedSettings={Boolean(group?.enableAdvanceGameSettings)}
          />
          ) : <span>Henter gruppedetaljer...</span>
        )}
      />
      )}
    </Page>
  );
};

const validationSchema = Yup.object({
  gameName: Yup.string()
    .max(40, 'Spillnavnet er for langt (maks. 40 tegn)')
    .required('Vennligst oppgi et spillnavn'),
  groupID: Yup.string()
    .required('Vennligst velg spillergruppe'),
  startTime: Yup.date()
    .required('Vennligst velg starttid'),
  maxTicketsPerPlayer: Yup.number()
    .required('Vennligst oppgi max antall billetter per spiller')
    .min(1, 'Hver spiller må ha minst 1 billett')
    .max(100, 'Maks. 100 billetter per spiller'),
  numberOfPrizes: Yup.number()
    .required('Vennligst oppgi antall premier')
    .max(10, 'Maks. antall premier per spill er 10')
    .min(1, 'Minimum 1 premie'),
  ticketPrice: Yup.number()
    .min(0, 'Billettprisen må være minimum 1')
    .max(100, 'Maks. 100 kr per billett')
    .required('Vennligst oppgi billettpris'),
  maxTickets: Yup.number()
    .min(1, 'Antall billetter i omløp må være mer enn 0')
    .max(100, 'Maks. antall billetter er 100'),
  prohibitNegativeBalance: Yup.boolean().notRequired(),
  randomness: Yup.number().min(1).max(5).notRequired(),
  spinSpeed: Yup.number().min(1).max(5).notRequired()
});

const GameForm = ({ groupId, groupDefaults, showAdvancedSettings }) => {
  const [error] = useState('');

  const history = useHistory();
  console.log("defaults", JSON.stringify(groupDefaults?.prohibitNegativeBalance ?? false))

  const initialGame = {
    gameName: '',
    groupID: groupId,
    startTime: new Date(Date.now() + 1000 * 60 * 60 * 24),
    maxTickets: groupDefaults?.maxTickets ?? 100,
    maxTicketsPerPlayer: groupDefaults?.maxTicketsPerPlayer ?? 3,
    numberOfPrizes: groupDefaults?.prizes ?? 3,
    ticketPrice: groupDefaults?.ticketPrice ?? 0,
    prohibitNegativeBalance: groupDefaults?.prohibitNegativeBalance ?? false,
    randomness: groupDefaults?.randomness ?? '',
    spinSpeed: groupDefaults?.spinSpeed ?? ''
  };

  return (
    <>
      <H1>Lag et nytt spill!</H1>
      <Formik
        initialValues={initialGame}
        validationSchema={validationSchema}
        onSubmit={(gameForm, { setSubmitting }) => {
          gamesAPI.add(gameForm)
            .then(gameId => {
              setSubmitting(false);
              history.push(`/game/${gameId}`);
            });
        }}
      >
        <Form>
          <FormField
            name="gameName"
            title="Navn på nytt spill"
        />
          <DateField
            name="startTime"
            title="Velg starttid"
            class=""
          />
          <GameFormDetails showAdvancedSettings={showAdvancedSettings} />
          <Button title="Lagre" type="submit" />
          <Button title="Tilbake" onClick={() => history.push(paths.DASHBOARD)} />
        </Form>
      </Formik>
      {error}
    </>
  );
};

NewGame.propTypes = {
  authUser: PropTypes.object,
  fetchGroupDetails: PropTypes.func.isRequired,
  groupDetailsRemoteData: PropTypes.object.isRequired
};

GameForm.propTypes = {
  groupId: PropTypes.string,
  groupDefaults: PropTypes.object,
  showAdvancedSettings: PropTypes.bool
};

const mapStateToProps = ({ session, games }) => ({
  authUser: session.authUser,
  groupDetailsRemoteData: games.groupDetailsRemoteData
});

const mapDispatchToProps = dispatch => ({
  fetchGroupDetails: a.fetchGroupDetails(dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(NewGame);
